import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointSelectAccountData,
  type EnrollmentJointSelectAccountSubmitInput,
  type EnrollmentJointSelectAccountSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint SelectAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointSelectAccountData>
 * @example const response = await getEnrollmentJointSelectAccountData()
 */
export const getEnrollmentJointSelectAccountData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointSelectAccountData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointSelectAccountData',
      query: `mutation getEnrollmentJointSelectAccountData($submitJointEnrollmentInput: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(submitJointEnrollmentInput: $submitJointEnrollmentInput) {
          activePerson {
            applicationId
            path
            progress
          }
        }
      }`,
    },
    {
      submitJointEnrollmentInput: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointSelectAccountData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointSelectAccountData = createQueryHook<undefined, EnrollmentJointSelectAccountData>(
  getEnrollmentJointSelectAccountData,
)

/**
 * Submit Enrollment Joint SelectAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointSelectAccountSubmitOutput>
 * @example const response = await submitEnrollmentJointSelectAccountData();
 */
export const submitEnrollmentJointSelectAccountData = async (
  enrollmentSelectAccountSubmitInput: EnrollmentJointSelectAccountSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointSelectAccountSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointSelectAccountData',
      query: `mutation submitEnrollmentJointSelectAccountData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          activePerson {
            path
            progress
          }
        }
      }`,
    },
    {
      input: enrollmentSelectAccountSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointSelectAccountSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointSelectAccountData = createMutationHook<
  EnrollmentJointSelectAccountSubmitInput,
  EnrollmentJointSelectAccountSubmitOutput
>(submitEnrollmentJointSelectAccountData)
